import {
  AdditionalCartDetailsContent,
  AdditionalCartDetailsTitle,
  AdditionalCartDetailsWrapper,
  BackWrapper,
  BottomWrapper,
  CardsWrapper,
  Container,
  IconWrapper,
  ProductsTitle,
  ProductsWrapper,
  RetryWrapper,
  StatusLabel,
  StatusOptionLabel,
  StatusOptionsContainer,
  StatusWrapper,
  SubTitle,
  Title,
  TitleWrapper,
  PricingLabel,
  PricingRow,
  PricingRowTotal,
  PricingValue,
  PricingValueTotal,
  PricingWrapper,
} from './SupplierSingleOrderPage.styled';
import { PencilSimple, Repeat } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import BackButton from 'components/BackButton/BackButton';
import { useTranslation } from 'react-i18next';
import { RoutesConfig } from 'navigation/routes';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useChangeOrderStatus,
  useGetSingleOrder,
  useRetryOrder,
} from 'pages/SupplierOrdersPage/hooks';
import {
  canRetryOrder,
  formatOrderStatus,
  getOrderColor,
  isOrderBeingSettled,
  isOrderToCheckWithSupplier,
} from 'utils/orderUtils';
import OrderStatusNote from 'components/OrderStatusNote/OrderStatusNote';
import SimpleWideCard from 'components/SimpleWideCard/SimpleWideCard';
import { ICartItem } from 'types/CartItem.types';
import ShoppingCartCard from 'pages/ShoppingCartPage/ShoppingCartCard/ShoppingCartCard';
import { useRef, useState } from 'react';
import { useOutsideAlerter } from 'utils/hooks/useOutsideAlerter';
import { statusOptions } from './constants';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import moment from 'moment';
import { getCountryNameByCode } from 'utils/countryUtils';
import { ConfirmModal } from 'components/Modals/ConfirmModal/ConfirmModal';
import { OrderStatus } from 'types/Orders.types';

const SupplierSingleOrderPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] =
    useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  // Successful by hand takes the order's sent lines out of the buyer's cart
  // (logix-core docs/ordering.md, "Changing an order"): asked first.
  const [isConfirmSuccessfulOpen, setIsConfirmSuccessfulOpen] =
    useState<boolean>(false);

  const { data, isFetching: isFetchingOrder } = useGetSingleOrder(
    typeof id === 'string' ? id : ''
  );

  const formatMoney = useFormatMoney(data?.currency);

  const { mutate: changeOrderStatus } = useChangeOrderStatus();
  const { mutate: retryOrder, isLoading: isRetrying } = useRetryOrder();

  // SENDING and UNKNOWN are Logyx core's to settle: no status is set by hand.
  const isStatusEditable = !!data && !isOrderBeingSettled(data.status);
  // A Check with supplier order is the supplier's to check: Successful if it is
  // in their system. Pending would hide it: off logyx-admin's list of orders to
  // look at, out of reach of a platform admin's resolve, and the buyer's cart
  // still refused (docs/BP-1219-ordering.md, §3).
  const offeredStatusOptions = isOrderToCheckWithSupplier(data)
    ? statusOptions.filter((option) => option.value !== OrderStatus.PENDING)
    : statusOptions;

  const statusWrapperRef = useRef<HTMLDivElement>();

  useOutsideAlerter(statusWrapperRef, () => setIsStatusDropdownOpen(false));

  const customerAddress = data?.company_buyer.address;

  const customerDetailsCardLeftData = {
    Name: data?.company_buyer.name,
    ...(customerAddress && {
      Address: `${customerAddress.street || ''} ${
        customerAddress.house_number || ''
      } ${customerAddress.city || ''}`.trim(),
      'Zip code': customerAddress.zip_code,
    }),
  };

  const customerDetailsCardRightData = {
    'E-mail': data?.company_buyer.email,
    'Phone number': data?.company_buyer.phone_number,
  };

  // Determine shipping type and data
  const shippingTypeLabel = t('Shipping Type');
  let shippingLeftData = {};
  let shippingRightData = {};

  if (data?.shipping_address?.is_default) {
    // Scenario 1: Default Address
    shippingRightData = {
      [shippingTypeLabel]: t('Default address'),
    };
  } else if (data?.shipping_address?.is_pickup) {
    // Scenario 2: Pickup
    shippingRightData = {
      [shippingTypeLabel]: t('Pick up'),
    };
  } else if (data?.shipping_address?.code) {
    // Scenario 3: Alternative Address
    const fullAddress = `${data?.shipping_address.address.street} ${
      data?.shipping_address.address.house_number || ''
    } ${data?.shipping_address.address.zip_code} ${
      data?.shipping_address.address.city
    } ${getCountryNameByCode(data?.shipping_address.address.country)}`;

    shippingLeftData = {
      [shippingTypeLabel]: t('Alternative address'),
      'Ship-to Code': data?.shipping_address.code,
      'Ship-to Contact': data?.shipping_address.contact_person,
    };
    shippingRightData = {
      'Ship-to Address': fullAddress,
    };
  } else if (data?.shipping_address) {
    // Scenario 4: Consumer Delivery
    const fullAddress = `${data?.shipping_address.address.street} ${
      data?.shipping_address.address.house_number || ''
    } ${data?.shipping_address.address.zip_code} ${
      data?.shipping_address.address.city
    } ${getCountryNameByCode(data?.shipping_address.address.country)}`;

    shippingLeftData = {
      [shippingTypeLabel]: t('Consumer Delivery'),
      'Consumer Name': data?.shipping_address.contact_person,
    };
    shippingRightData = {
      'E-mail address': data?.shipping_address.email,
      'Phone number': data?.shipping_address.phone_number,
      Address: fullAddress,
    };
  }

  const orderDetailsCardLeftData = {
    'Logyx Order ID': data?.number,
    ...(data?.reference && {
      'Customer Order Reference': data?.reference,
    }),
    ...(data?.internal_number && {
      'Supplier Order Number': data?.internal_number,
    }),
    ...(data?.requested_delivery_date && {
      'Requested Delivery Date': moment(data?.requested_delivery_date).format(
        'DD.MM.YYYY'
      ),
    }),
    ...shippingLeftData,
  };

  const orderDetailsCardRightData = {
    ...(data?.confirmed_delivery_date && {
      'Confirmed Delivery Date': moment(data?.confirmed_delivery_date).format(
        'DD.MM.YYYY'
      ),
    }),
    'Sales Amount': formatMoney(data?.total_net_price),
    ...shippingRightData,
  };

  return (
    <Container>
      <BackWrapper>
        <BackButton
          destination={t('Orders')}
          onClick={() =>
            navigate(RoutesConfig.SupplierOrders.fullPath, { replace: true })
          }
        />
      </BackWrapper>
      <TitleWrapper>
        <Title>{data?.internal_number}</Title>
        {!data?.internal_number && (
          <SubTitle>
            {t('Logyx Order ID')}: ({data?.number})
          </SubTitle>
        )}
        <StatusWrapper ref={statusWrapperRef}>
          <StatusLabel
            $color={getOrderColor(data)}
            $editable={isStatusEditable}
            data-testid="order-status"
            onClick={
              isStatusEditable
                ? () => setIsStatusDropdownOpen(!isStatusDropdownOpen)
                : undefined
            }
          >
            {t(formatOrderStatus(data))}
          </StatusLabel>
          {isStatusEditable && (
            <IconWrapper data-testid="order-status-edit">
              <Icon
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                svg={PencilSimple}
                color={COLORS.GREEN}
                size={20}
              />
            </IconWrapper>
          )}

          {isStatusEditable && isStatusDropdownOpen && (
            <StatusOptionsContainer>
              {offeredStatusOptions.map((option) => {
                return (
                  <StatusOptionLabel
                    isSelected={option.value === data?.status}
                    onClick={() => {
                      if (option.value !== data?.status) {
                        setIsStatusDropdownOpen(false);
                        if (option.value === OrderStatus.SUCCESSFUL) {
                          setIsConfirmSuccessfulOpen(true);
                          return;
                        }
                        changeOrderStatus({
                          id: data?.id || '',
                          status: option.value,
                        });
                      }
                    }}
                    key={option.id}
                  >
                    {t(option.label)}
                  </StatusOptionLabel>
                );
              })}
            </StatusOptionsContainer>
          )}
        </StatusWrapper>
        {canRetryOrder(data) && (
          <RetryWrapper
            $disabled={isRetrying}
            data-testid="order-retry"
            onClick={isRetrying ? undefined : () => setIsConfirmModalOpen(true)}
          >
            {t('Retry')}
            <IconWrapper>
              <Icon svg={Repeat} color={COLORS.GREEN} size={20} weight="bold" />
            </IconWrapper>
          </RetryWrapper>
        )}
      </TitleWrapper>

      <OrderStatusNote order={data} isSupplier />

      <CardsWrapper>
        <SimpleWideCard
          cardTitle={t('Customer details')}
          leftSubtitle={t('GENERAL')}
          rightSubtitle={t('CONTACT')}
          leftData={customerDetailsCardLeftData}
          rightData={customerDetailsCardRightData}
          isLoading={isFetchingOrder}
        />
        <SimpleWideCard
          cardTitle={t('Order details')}
          leftSubtitle={''}
          rightSubtitle={''}
          leftData={orderDetailsCardLeftData}
          rightData={orderDetailsCardRightData}
          isLoading={isFetchingOrder}
        />
      </CardsWrapper>

      <ProductsWrapper>
        <ProductsTitle>{t('Products')}</ProductsTitle>
        {data?.lines?.map((item: ICartItem, index: number) => (
          <ShoppingCartCard
            item={item}
            key={`cart-item-${index}`}
            isPreview={true}
            isGrossVisible={data.is_gross_visible}
            currency={data?.currency}
          />
        ))}

        <BottomWrapper>
          {data?.additional_details && (
            <AdditionalCartDetailsWrapper>
              <AdditionalCartDetailsTitle>
                {t('Additional cart details')}
              </AdditionalCartDetailsTitle>
              <AdditionalCartDetailsContent>
                {data.additional_details}
              </AdditionalCartDetailsContent>
            </AdditionalCartDetailsWrapper>
          )}

          <PricingWrapper>
            {data?.total_gross_price !== data?.total_net_price &&
              data?.is_gross_visible && (
                <PricingRow>
                  <PricingLabel>{t('Subtotal')}:</PricingLabel>
                  <PricingValue>
                    {formatMoney(data?.total_gross_price)}
                  </PricingValue>
                </PricingRow>
              )}
            {data?.total_gross_price !== data?.total_net_price &&
              data?.is_gross_visible && (
                <PricingRow>
                  <PricingLabel>{t('Discount')}:</PricingLabel>
                  <PricingValue>
                    {formatMoney(data?.total_model_discount)}
                  </PricingValue>
                </PricingRow>
              )}
            <PricingRow>
              <PricingLabel>{t('Total excl. VAT')}:</PricingLabel>
              <PricingValue>{formatMoney(data?.total_net_price)}</PricingValue>
            </PricingRow>
            <PricingRow>
              <PricingLabel>
                {`${t('VAT')} (${data?.vat_percentage}%)`}:
              </PricingLabel>
              <PricingValue>{formatMoney(data?.vat)}</PricingValue>
            </PricingRow>
            <PricingRowTotal>
              <PricingLabel>{t('Total incl. VAT')}:</PricingLabel>
              <PricingValueTotal>
                {formatMoney(data?.total_net_price_incl_vat)}
              </PricingValueTotal>
            </PricingRowTotal>
          </PricingWrapper>
        </BottomWrapper>
      </ProductsWrapper>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        title={t('Confirm retry')}
        description={t('Are you sure you want to retry ordering?')}
        onConfirm={() => {
          if (!isRetrying) retryOrder({ id: data?.id });
          setIsConfirmModalOpen(false);
        }}
        onCancel={() => setIsConfirmModalOpen(false)}
      />

      <ConfirmModal
        isOpen={isConfirmSuccessfulOpen}
        setIsOpen={setIsConfirmSuccessfulOpen}
        title={t('Set the status to Successful')}
        description={t(
          "Only do this once the order is already in your system. Its products leave the buyer's cart, and it can no longer be sent again."
        )}
        onConfirm={() => {
          changeOrderStatus({
            id: data?.id || '',
            status: OrderStatus.SUCCESSFUL,
          });
          setIsConfirmSuccessfulOpen(false);
        }}
        onCancel={() => setIsConfirmSuccessfulOpen(false)}
      />
    </Container>
  );
};

export default SupplierSingleOrderPage;
